import {Injectable} from '@angular/core';
import {EnvironmentService} from './environment.service';
import {HttpClient} from '@angular/common/http';
import {forkJoin, Observable, throwError} from 'rxjs';
import {catchError, map, mergeMap, shareReplay} from 'rxjs/operators';
import fixArray from '../../../utils/fixArray';
import {News} from '../interfaces/news';
import {ProjectService} from './project.service';

@Injectable({
  providedIn: 'root'
})
export class NewsService {

  constructor(private _environmentService: EnvironmentService,
              private _projectService: ProjectService,
              private _http: HttpClient) {
  }

  httpCache = new Map<string, Observable<any>>();

  formatImage(image: string): string {
    if (image.startsWith('http')) {
      return image;
    }
    return `https://cdn.otosrl.com/resources/uploadedNews/${image}`;
  }

  formatNews(news: any): News {
    return {
      date: new Date(news.data * 1000),
      image: news.foto ? this.formatImage(news.foto) : undefined,
      id: parseInt(news.id, 10),
      searchId: news.ricerca,
      subtitle: news.sottotitolo,
      text: news.testo,
      title: news.titolo,
      url: news.url.trim(),
      visible: news.visualizza === '1'
    };
  }

  newsValidityFilter(news: News): boolean {
    return news ? !!news.searchId : false;
  }

  formatAllNews(news: any[]): News[] {
    return news.map(e => this.formatNews(e));
  }

  getNewsItem(searchId: string): Observable<News> {
    return this.getNews().pipe(
      map(e => e.find(x => x.searchId === searchId)),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err))
    );
  }

  getNews(): Observable<News[]> {
    return forkJoin([
      this.getNetworkNews(),
      this.getAgencyNews()
    ]).pipe(
      map(e => [
        ...e[0], ...e[1]
      ].filter(this.newsValidityFilter)),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err))
    );
  }

  getAgencyNews(): Observable<News[]> {
    return forkJoin([
      this._environmentService.getEnvironment(),
      this._projectService.getAgencyApikey(),
    ]).pipe(
      mergeMap(e => {
        const url = `${e[0].otoApiUrl}/index.php/cms/${e[1]}/news`;
        if (!this.httpCache.get(url)) {
          this.httpCache.set(url, this._http.get<any>(url).pipe(shareReplay({bufferSize: 1, refCount: true})));
        }
        return this.httpCache.get(url);
      }),
      map(e => this.formatAllNews(fixArray(e))),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err))
    );
  }

  getNetworkNews(): Observable<News[]> {
    return forkJoin([
      this._environmentService.getEnvironment(),
      this._environmentService.getNetworkApikey(),
    ]).pipe(
      mergeMap(e => {
        const url = `${e[0].otoApiUrl}/index.php/cms/${e[1]}/news`;
        if (!this.httpCache.get(url)) {
          this.httpCache.set(url, this._http.get<any>(url).pipe(shareReplay({bufferSize: 1, refCount: true})));
        }
        return this.httpCache.get(url);
      }),
      map(e => this.formatAllNews(fixArray(e))),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err))
    );
  }
}
